import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { getSharedOnboarding } from "@/lib/api";
import { toast } from "sonner";
import OnboardingCanvasStage from "@/components/onboarding/OnboardingCanvasStage";
import {
  migrateSlide,
  normalizedFireworksAsciiColor,
  normalizedSlideEnterEffect,
  slideCanvasBackgroundStyle,
} from "@/lib/onboardingCanvas";
import { fireOnboardingSlideEnterConfetti, onboardingPrefersReducedMotion } from "@/lib/onboardingConfetti";
import OnboardingSlideEnterFireworks from "@/components/onboarding/OnboardingSlideEnterFireworks";
import OnboardingSlideEnterFirew0rksAscii from "@/components/onboarding/OnboardingSlideEnterFirew0rksAscii";

export default function OnboardingPublicPage() {
  const { shareSlug } = useParams();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const confettiRunIdRef = useRef(0);
  const swipeStartRef = useRef(null);
  const [fireworksPlayId, setFireworksPlayId] = useState(0);
  const endFireworks = useCallback(() => setFireworksPlayId(0), []);
  const [fireworksAsciiPlayId, setFireworksAsciiPlayId] = useState(0);
  const endFireworksAscii = useCallback(() => setFireworksAsciiPlayId(0), []);

  const slides = useMemo(() => {
    if (!data || !Array.isArray(data.slides)) return [];
    return data.slides.map(migrateSlide);
  }, [data]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!shareSlug || !password.trim() || loading) return;
    setLoading(true);
    setError("");
    try {
      const res = await getSharedOnboarding(shareSlug, password.trim());
      setData(res);
      setSlideIndex(0);
    } catch (err) {
      const msg = err.response?.data?.detail || "Contraseña incorrecta o enlace no válido";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!data || slides.length <= 1) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") setSlideIndex((i) => Math.min(slides.length - 1, i + 1));
      if (e.key === "ArrowLeft") setSlideIndex((i) => Math.max(0, i - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [data, slides.length]);

  /** Confetti al entrar (vista pública, respeta reduced-motion). */
  useEffect(() => {
    if (!data || !slides.length) return;
    const safeIdx = Math.min(Math.max(0, slideIndex), slides.length - 1);
    const current = slides[safeIdx];
    if (normalizedSlideEnterEffect(current) !== "confetti") return;
    if (onboardingPrefersReducedMotion()) return;
    const runId = ++confettiRunIdRef.current;
    let cancelled = false;
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled || runId !== confettiRunIdRef.current) return;
        fireOnboardingSlideEnterConfetti();
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [data, slides, slideIndex]);

  /** Fuegos artificiales (tsParticles preset) al entrar en la diapositiva. */
  useEffect(() => {
    if (!data || !slides.length) return;
    const safeIdx = Math.min(Math.max(0, slideIndex), slides.length - 1);
    const current = slides[safeIdx];
    if (normalizedSlideEnterEffect(current) !== "fireworks" || onboardingPrefersReducedMotion()) {
      setFireworksPlayId(0);
      return;
    }
    setFireworksPlayId((n) => n + 1);
  }, [data, slides, slideIndex]);

  /** Fuegos ASCII (animación firew0rks) al entrar en la diapositiva. */
  useEffect(() => {
    if (!data || !slides.length) return;
    const safeIdx = Math.min(Math.max(0, slideIndex), slides.length - 1);
    const current = slides[safeIdx];
    if (normalizedSlideEnterEffect(current) !== "fireworks_ascii" || onboardingPrefersReducedMotion()) {
      setFireworksAsciiPlayId(0);
      return;
    }
    setFireworksAsciiPlayId((n) => n + 1);
  }, [data, slides, slideIndex]);

  if (!shareSlug) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-neutral-100 px-4">
        <p className="text-sm text-neutral-500">Enlace no válido.</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-neutral-100 px-4">
        <div className="w-full max-w-sm">
          <Card className="border-0 shadow-lg shadow-neutral-900/5">
            <CardContent className="pt-8 pb-8 px-6 space-y-5">
              <div className="text-center space-y-1">
                <h1 className="text-lg font-semibold tracking-tight text-neutral-900">Onboarding</h1>
                <p className="text-sm text-neutral-500">Introduce la contraseña que te han facilitado.</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-3">
                <Input
                  type="password"
                  autoComplete="off"
                  placeholder="Contraseña"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 bg-white"
                />
                {error && <p className="text-xs text-red-600 text-center">{error}</p>}
                <Button type="submit" className="w-full h-11 rounded-full" disabled={loading || !password.trim()}>
                  {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" aria-hidden />}
                  Ver presentación
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const total = slides.length;
  const safeIndex = total ? Math.min(Math.max(slideIndex, 0), total - 1) : 0;
  const slide = slides[safeIndex];
  const widgets = slide?.widgets || [];
  const hasWidgets = widgets.length > 0;

  // Móvil: deslizar horizontalmente cambia de diapositiva (umbral 50px y más horizontal que vertical)
  const onTouchStart = (e) => {
    const t = e.touches[0];
    swipeStartRef.current = t ? { x: t.clientX, y: t.clientY } : null;
  };
  const onTouchEnd = (e) => {
    const start = swipeStartRef.current;
    const t = e.changedTouches[0];
    swipeStartRef.current = null;
    if (!start || !t || total < 2) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    setSlideIndex((i) => (dx < 0 ? Math.min(total - 1, i + 1) : Math.max(0, i - 1)));
  };

  const pageBgStyle = slide ? slideCanvasBackgroundStyle(slide, "#0a0a0a") : { background: "#0a0a0a" };
  const fireworksAsciiColor = slide ? normalizedFireworksAsciiColor(slide) : "#facc15";

  return (
    <div className="fixed inset-0 z-0 flex flex-col" style={pageBgStyle} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {fireworksPlayId > 0 ? (
        <OnboardingSlideEnterFireworks key={`ob-fw-${fireworksPlayId}`} playId={fireworksPlayId} onEnded={endFireworks} />
      ) : null}
      {fireworksAsciiPlayId > 0 ? (
        <div className="pointer-events-none fixed inset-0 z-[1] overflow-hidden">
          <OnboardingSlideEnterFirew0rksAscii
            key={`ob-fw-ascii-${fireworksAsciiPlayId}`}
            playId={fireworksAsciiPlayId}
            color={fireworksAsciiColor}
            onEnded={endFireworksAscii}
            fullscreen
          />
        </div>
      ) : null}
      {total === 0 ? (
        <div className="flex flex-1 items-center justify-center text-neutral-400 text-sm">Esta presentación no tiene diapositivas.</div>
      ) : (
        <>
          <div
            className="relative z-[2] flex flex-1 min-h-0 w-full ob-slide-fade"
            key={`${slide?.id}-${safeIndex}`}
          >
            <OnboardingCanvasStage
              widgets={widgets}
              layoutSlide={slide}
              background={slide?.background || "#ffffff"}
              readOnly
              animated={hasWidgets}
              fullscreen
              className="h-full w-full min-h-0"
            />
            {!hasWidgets ? (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <p className="text-sm text-neutral-400 bg-white/90 px-4 py-2 rounded-full border border-neutral-200/80">Diapositiva vacía</p>
              </div>
            ) : null}
          </div>

          {total > 1 ? (
            <div className="fixed bottom-[calc(env(safe-area-inset-bottom)+1.5rem)] left-1/2 z-[600] flex -translate-x-1/2 items-center gap-2 sm:bottom-8">
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="h-12 w-12 rounded-full border border-white/20 bg-white/90 shadow-lg backdrop-blur-sm hover:bg-white"
                disabled={safeIndex <= 0}
                onClick={() => setSlideIndex((i) => Math.max(0, i - 1))}
                aria-label="Anterior"
              >
                <ChevronLeft className="w-6 h-6 text-neutral-800" aria-hidden />
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="h-12 w-12 rounded-full border border-white/20 bg-white/90 shadow-lg backdrop-blur-sm hover:bg-white"
                disabled={safeIndex >= total - 1}
                onClick={() => setSlideIndex((i) => Math.min(total - 1, i + 1))}
                aria-label="Siguiente"
              >
                <ChevronRight className="w-6 h-6 text-neutral-800" aria-hidden />
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
